// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from "react"
import ReactDOM from "react-dom"
import "./BaleView.scss"

import { useTranslation } from "react-i18next";
import { AuthContext } from '../../context/AuthContext.js';
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { firestore, storage } from "../../context/FirebaseConfig.js";

import PropertyField from "../PropertyField/PropertyField.jsx";
import DeliveryImagePreview from "../DeliveryImagePreview/DeliveryImagePreview.jsx";
import BaleLabelingPanel from "./BaleLabelingPanel.jsx";
import ZoomImage from "../ZoomImage/ZoomImage.jsx";
import { authAndDownloadImage } from "../../shared_components/utils/ImgUtils.js"

import close_icon           from '../../assets/icons/close.svg';
import back_icon_white      from '../../assets/icons/white/back_small.png';
import forward_icon_white   from '../../assets/icons/white/forward_small.png';
import download_icon        from '../../assets/icons/white/download.png';
import notes_icon           from '../../assets/icons/lightblue/document.png';
import alert_off_icon       from '../../assets/icons/white/alert_no_circle.png';
import alert_on_icon        from '../../assets/icons/alert_circle.png';

export default function BaleView({deliveryId, baleId, deliveryImages, onClose, goToPreviousBale, goToNextBale, showDeliveryImage}) {
    const { i18n, t } = useTranslation();
    const { currentUser } = React.useContext(AuthContext);
    const [bale, setBale] = React.useState(null);
    const unsubscribe = React.useRef(null);

    // Create a ref for the portal container
    const portalContainerRef = React.useRef(null);
    
    // Create portal container on mount
    React.useEffect(() => {
        // Check if portal container already exists
        let container = document.getElementById('bale-view-portal');
        if (!container) {
            container = document.createElement('div');
            container.id = 'bale-view-portal';
            document.body.appendChild(container);
        }
        
        portalContainerRef.current = container;
        
        // Clean up on unmount
        return () => {
            if (portalContainerRef.current && portalContainerRef.current.childNodes.length === 0) {
                document.body.removeChild(portalContainerRef.current);
            }
        };
    }, []);

    // Subscribe to bale document
    React.useEffect(() => {
        // Unsubscribe from previous query
        if (unsubscribe.current) unsubscribe.current();

        // Subscribe to bale document
        const baleRef = doc(firestore, "clients", currentUser.company.company, "deliveries", deliveryId, "bales", baleId);
        const newUnsubscribe = onSnapshot(baleRef, (snapshot) => {
            setBale(snapshot.data());
        });
        unsubscribe.current = () => newUnsubscribe();
    }, [deliveryId, baleId]);

    // Handle field changes
    async function handleBaleFieldChange(event) {
        /** Change value of a bale field and update on database */
        const { name, value } = event.target;
        const baleRef = doc(firestore, "clients", currentUser.company.company, "deliveries", deliveryId, "bales", baleId);
        await updateDoc(baleRef, {[name]: value});
    }

    // Add event listener for keyboard navigation
    React.useEffect(() => {
        /* Add event listener for arrow keys to navigate between images */
        function handleKeyDown(event) {
            // Check for left arrow key
            if (event.keyCode === 37) {
                goToPreviousBale();
            }
    
            // Check for right arrow key
            if (event.keyCode === 39) {
                goToNextBale();
            }
        }
        document.addEventListener('keydown', handleKeyDown);
        return () => { document.removeEventListener('keydown', handleKeyDown); }
    }, [baleId]);

    // If portal container isn't ready, return null
    if (!portalContainerRef.current) return null;

    // The JSX to render
    const baleViewContent = (
        <div className="fullscreen-bale-modal">
            <div className="fullscreen-modal--buttons">
                <div className="fullscreen-modal--button fullscreen-modal--next" onClick={goToPreviousBale}>
                    <img className="fullscreen-modal--button-icon" src={back_icon_white}/>
                </div>
            </div>
            <div className="fullscreen-modal--center">
                <div className="fullscreen-modal--img-wrapper">
                    <ZoomImage image_url={bale?.image_URL} />
                </div>
                <div className="center-footer">
                    <PropertyField 
                        icon={notes_icon} 
                        title={t("property_notes").toUpperCase()} 
                        value={bale?.notes} 
                        name="notes"
                        type="textarea"
                        handleChange={handleBaleFieldChange}
                        editable={true}
                        grid_span={1}
                    />
                    <div className="delivery-image-preview-container">
                        <DeliveryImagePreview
                            deliveryImageData={deliveryImages?.[bale?.delivery_image_id]}
                            displayBale={bale}
                            onClick={() => showDeliveryImage(bale?.delivery_image_id)}
                        />
                    </div>
                    <div className="bale-alert"
                        onClick={() => {handleBaleFieldChange({target: {name: "flagged", value: !bale?.flagged}})}}
                        style={{ opacity: bale?.flagged ? 1 : 0.5 }}
                    >
                        <img src={bale?.flagged ? alert_on_icon : alert_off_icon}/>
                    </div>
                </div>
                
            </div>
            <div className="fullscreen-modal--buttons">
                <div className="fullscreen-modal--button" onClick={onClose}>
                    <img className="fullscreen-modal--button-icon" src={close_icon}/>
                </div>
                <div className="fullscreen-modal--button" onClick={() => {
                    authAndDownloadImage(storage, bale?.image_URL, `bale_${baleId}.jpg`)}}>
                    <img className="fullscreen-modal--button-icon" src={download_icon}/>
                </div>
                <div className="fullscreen-modal--button fullscreen-modal--next" onClick={goToNextBale}>
                    <img className="fullscreen-modal--button-icon" src={forward_icon_white}/>
                </div>
            </div>
            
            {currentUser.info.user_flags && ['labeler', 'admin'].some(flag => currentUser.info.user_flags.includes(flag)) &&
            <BaleLabelingPanel 
                baleId={baleId}
                deliveryId={deliveryId}
                bale={bale} 
                getNext={goToNextBale}
            />
            }
        </div>
    );

    // Render using portal
    return ReactDOM.createPortal(baleViewContent, portalContainerRef.current);
}